import React, { FC, useCallback, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import {
	downloadRFPCSV,
	refreshRFPCount,
	requestsForProposalRestCrudThunksForBuyer,
	requestsForProposalRestCrudThunksForSupplier,
} from '../../thunks/requests_for_proposal_thunks';
import { REQUESTS_FOR_PROPOSAL_CRUD_ACTION_CREATORS } from '../../actions/requests_for_proposal_actions';
import { EmptyState } from '../empty_state/EmptyState';
import RFPRowDisplay from '../rfp_row_display/RFPRowDisplay';
import { ROLE_TYPES } from '../../utils/DataConstants';
import RFPPipelineFilters from './RFPPipelineFilters';
import { Button, message } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import { getLinkWIthBackLinkParams } from '../../utils/HistoryUtils';
import ReduxDataTable from '../redux_data_table/ReduxDataTable';
import { useFilters } from '../../hooks/useFilters';
import { getRFPFilterConfig } from '../../utils/RFPFilterUtils';
import { updateFiltersEntities } from '../../thunks/thunks_wrapper';

const REQUESTS_FOR_PROPOSAL_OVERVIEW_TARGET_COLLECTION = 'requestsForProposalIndex';
const RFP_ADVANCED_FILTERS = 'rfpAdvancedFilters';

const RequestsForProposalIndexPage: FC<any> = ({
	history,
	updateFilters,
	requestsForProposals,
	clearAndUpdateFilters,
	userType,
	currentUser,
	refreshCounts,
	downloadCSV,
	location,
}) => {
	const [csvDownloading, setCsvDownloading] = useState(false);

	const columns = [
		{
			title: 'Name',
			dataIndex: 'title',
			render: (text, record) => <RFPRowDisplay rfp={record} />,
		},
	];

	const onRow = (record) => ({
		onClick: () =>
			history.push(
				getLinkWIthBackLinkParams(
					location,
					'',
					`/${userType}/requestsForProposal/detail/${record.id}`
				)
			),
	});

	const rfpFilters = useFilters({
		filterTargetCollectionName: RFP_ADVANCED_FILTERS,
		overviewCollectionName: REQUESTS_FOR_PROPOSAL_OVERVIEW_TARGET_COLLECTION,
		updateFilters,
	});

	const filterConfig = useMemo(
		() => getRFPFilterConfig({ filters: rfpFilters, userType, currentUser }),
		[rfpFilters, userType, currentUser]
	);

	const onDownloadCSV = useCallback(() => {
		const filters = requestsForProposals[REQUESTS_FOR_PROPOSAL_OVERVIEW_TARGET_COLLECTION].filters;
		setCsvDownloading(true);
		downloadCSV(filters)
			.catch(message.error)
			.finally(() => setCsvDownloading(false));
	}, [downloadCSV, requestsForProposals]);

	return (
		<ReduxDataTable
			targetCollectionName={REQUESTS_FOR_PROPOSAL_OVERVIEW_TARGET_COLLECTION}
			updateFilters={updateFilters}
			stateSlice={requestsForProposals}
			clearAndUpdateFilters={clearAndUpdateFilters}
			refreshCounts={refreshCounts}
			pipeLineFilters={RFPPipelineFilters}
			filterConfig={filterConfig}
			entityCollectionName="requests_for_proposal"
			tableColumns={columns}
			onTableRow={onRow}
			initialPagination={{ current: 1, pageSize: 10 }}
			fetchData={
				userType === ROLE_TYPES.SUPPLIER
					? requestsForProposalRestCrudThunksForSupplier
					: requestsForProposalRestCrudThunksForBuyer
			}
			handleQueryParams
			rightActions={
				<Button
					style={{ marginRight: 12 }}
					size="large"
					icon={<DownloadOutlined />}
					className="inline-block-visible-md"
					loading={csvDownloading}
					onClick={onDownloadCSV}
				>
					<span className="inline-block-visible-xxl">Export CSV</span>
				</Button>
			}
			emptyState={
				<EmptyState
					graphic={
						<img
							style={{ marginBottom: 8 }}
							src="https://s3.amazonaws.com/mock-data-assets/categories/images/letter owl.svg"
							alt={`There are no request for proposals for you!`}
						/>
					}
					headline={`There are no request for proposals for you!`}
					body={null}
				/>
			}
		/>
	);
};

const mapStateToProps = (state) => ({
	currentUser: state.session.currentUser,
	requestsForProposals: state.requests_for_proposal,
});

const mapDispatchToProps = (dispatch, ownProps) => ({
	updateFilters: (filters) =>
		dispatch(
			updateFiltersEntities(
				ownProps.userType,
				'requests_for_proposal',
				ownProps.userType === ROLE_TYPES.SUPPLIER
					? requestsForProposalRestCrudThunksForSupplier
					: requestsForProposalRestCrudThunksForBuyer,
				refreshRFPCount(ownProps.userType)
			)(
				ownProps.handleQueryParams,
				ownProps.history,
				REQUESTS_FOR_PROPOSAL_OVERVIEW_TARGET_COLLECTION,
				filters,
				false
			)
		),
	clearAndUpdateFilters: (filters, targetCollectionName) =>
		dispatch(
			REQUESTS_FOR_PROPOSAL_CRUD_ACTION_CREATORS.clearAndUpdateFilters(
				filters,
				targetCollectionName
			)
		),
	refreshCounts: (filters) => dispatch(refreshRFPCount(ownProps.userType)(filters)),
	downloadCSV: (filters) => dispatch(downloadRFPCSV(ownProps.userType)(filters)),
});

const ComponentWithoutUserType = withRouter<any, any>(
	connect(mapStateToProps, mapDispatchToProps)(RequestsForProposalIndexPage)
);

export default connect(
	(state) => ({
		userType: (state as any).session.userType,
	}),
	() => ({})
)(ComponentWithoutUserType);
